import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Handshake, Store, Users, Camera } from 'lucide-react';

const collaborationTypes = [
  { key: 'wholesale', label: 'Wholesale / Stockist', icon: Store, description: 'Cafes, restaurants, retailers & grocers' },
  { key: 'brand', label: 'Brand Collaboration', icon: Handshake, description: 'Co-branded products, gift boxes & partnerships' },
  { key: 'influencer', label: 'Creator / Influencer', icon: Camera, description: 'Content creators, bloggers & social media' },
  { key: 'beekeeper', label: 'Beekeeper', icon: Users, description: 'Fellow beekeepers & honey producers' },
  { key: 'apiary', label: 'Apiary', icon: Store, description: 'Apiary sites, locations & land access' },
];

const Collaborate = () => {
  const [form, setForm] = useState({ name: '', email: '', business: '', type: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.type || !form.message.trim()) {
      setError('Please fill in all required fields and select a collaboration type.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/collaborate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        setSent(true);
        setForm({ name: '', email: '', business: '', type: '', message: '' });
      } else {
        setError('Failed to send. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please email us at sales@rickysbees.com.au');
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen pt-32 pb-24">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 text-center" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="collaborate-heading">
            Collaborate with Us
          </h1>
          <p className="text-foreground/70 text-center mb-12 max-w-xl mx-auto">
            We're always looking for like-minded partners who share our passion for quality, community and all things Australian honey.
          </p>

          {sent ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-16 p-8 rounded-2xl border border-border bg-muted/30" data-testid="collaborate-success">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                <Handshake className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>We've Got Your Details!</h2>
              <p className="text-foreground/70">Thanks for your interest. We'll be in touch soon to chat about how we can work together.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="collaborate-form">
              {/* Collaboration Type Selection */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-3">What type of collaboration are you interested in? *</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {collaborationTypes.map(({ key, label, icon: Icon, description }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, type: key }))}
                      className={`flex items-start space-x-3 p-4 rounded-xl border-2 transition-all text-left ${
                        form.type === key
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid={`collab-type-${key}`}
                    >
                      <Icon className={`w-6 h-6 mt-0.5 flex-shrink-0 ${form.type === key ? 'text-primary' : 'text-muted-foreground'}`} />
                      <div>
                        <p className="font-semibold text-foreground text-sm">{label}</p>
                        <p className="text-xs text-foreground/60">{description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Form Fields */}
              <div className="p-8 rounded-2xl border border-border bg-muted/30 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Your Name *</label>
                    <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="John Smith" className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm" data-testid="collab-name-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="john@example.com" className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm" data-testid="collab-email-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Business / Brand Name</label>
                  <input type="text" name="business" value={form.business} onChange={handleChange} placeholder="Your business or brand (if applicable)" className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm" data-testid="collab-business-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Tell us about your idea *</label>
                  <textarea name="message" value={form.message} onChange={handleChange} rows={5} placeholder="What did you have in mind? We'd love to hear your ideas..." className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm resize-none" data-testid="collab-message-input" />
                </div>
                {error && <p className="text-red-500 text-sm" data-testid="collab-error">{error}</p>}
                <button type="submit" disabled={sending} className="w-full px-8 py-4 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-all duration-300 font-semibold text-lg flex items-center justify-center space-x-2 disabled:opacity-50" data-testid="collab-submit-button">
                  <Send className="w-5 h-5" />
                  <span>{sending ? 'Sending...' : 'Submit Enquiry'}</span>
                </button>
              </div>
            </form>
          )}

          <div className="text-center mt-8">
            <p className="text-sm text-foreground/60">
              Prefer email? Reach out at <a href="mailto:sales@rickysbees.com.au" className="text-primary font-semibold hover:underline">sales@rickysbees.com.au</a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Collaborate;
